import { useRef, useState, useEffect, useCallback } from 'react';
import Environment from '../utils/Environment';

interface UndoState {
    rows: any[];
    currentPage: number;
}

export function useTableHandlers(columns: any[], tabType?: string, isActive: boolean = true, selectedDC?: string) {
    const [rows, setRows] = useState<any[]>(() => {
        const filledRows: any[] = [];
        while (filledRows.length < 25) {
            const emptyRow: any = {};
            columns.forEach(col => {
                emptyRow[col.key] = '';
            });
            filledRows.push(emptyRow);
        }
        return filledRows;
    });
    
    // Undo functionality
    const [undoStack, setUndoStack] = useState<UndoState[]>([]);
    const [redoStack, setRedoStack] = useState<UndoState[]>([]);
    const maxUndoSteps = 50; // Track last 50 operations only
    
    const [editingCell, setEditingCell] = useState<{ row: number; col: string } | null>(null);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const tableRef = useRef<HTMLTableElement>(null) as React.RefObject<HTMLTableElement>;
    const resizingColRef = useRef<number | null>(null);

    // Helper function to validate Load Group values
    const isValidLoadGroupValue = (value: any): boolean => {
        if (!value && value !== 0) return true; // Empty values are allowed
        
        const stringValue = String(value).trim();
        if (stringValue === '') return true; // Empty string is allowed
        
        // Check if this is a Custom IN02 DC
        const customIN02DCs = Environment.getCustomIN02DCList();
        const isCustomIN02DC = tabType === 'IN02' && selectedDC && customIN02DCs.includes(selectedDC);

        // Check if this is a Combined IN02 DC (e.g. WAUB)
        const combinedIN02DCs = Environment.getCombinedIN02DCList();
        const isCombinedIN02DC = tabType === 'IN02' && selectedDC && combinedIN02DCs.includes(selectedDC);
        
        if (isCustomIN02DC) {
            // Custom IN02: Allow any numerical value > 0
            const digitsOnly = /^[0-9]+$/.test(stringValue);
            if (!digitsOnly) {
                return false;
            }
            const numValue = parseInt(stringValue, 10);
            return numValue > 0;
        } else if (isCombinedIN02DC) {
            // Combined IN02 (e.g. WAUB): Allow any numerical value > 0
            const digitsOnly = /^[0-9]+$/.test(stringValue);
            if (!digitsOnly) {
                return false;
            }
            const numValue = parseInt(stringValue, 10);
            return numValue > 0;
        } else {
            // Normal IN02: Must be 1 or 2 digits (1-99)
            const oneOrTwoDigits = /^\d{1,2}$/.test(stringValue);
            if (!oneOrTwoDigits) {
                return false;
            }
            
            // Parse and validate range: must be between 1-99
            const numValue = parseInt(stringValue, 10);
            return numValue > 0 && numValue <= 99;
        }
    };

    // State for tracking validation errors
    const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
    const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>, rowIndex: number, key: string) => {
        // Save current state before making changes
        saveToUndoStack();
        
        const newRows = [...rows];
        let value = e.target.value;
        
        // Clean single quotes from Shipment ID values
        if (key === 'Shipment ID') {
            value = value.replace(/'/g, '');
        }
        
        // Convert Facility values to uppercase for IN02 tab
        if (tabType === 'IN02' && key === 'Facility') {
            value = value.toUpperCase();
        }
        
        newRows[rowIndex] = {
            ...newRows[rowIndex],
            [key]: value
        };
        setRows(newRows);

        // Real-time validation for Load Group fields
        const loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];
        if (tabType === 'IN02' && loadGroupFields.includes(key)) {
            const cellKey = `${rowIndex}-${key}`;
            const newValidationErrors = { ...validationErrors };
            
            if (value && value.trim() !== '' && !isValidLoadGroupValue(value)) {
                newValidationErrors[cellKey] = true;
            } else {
                delete newValidationErrors[cellKey];
            }
            
            setValidationErrors(newValidationErrors);
        }
    };

    const handleClick = (row: number, col: string) => {
        setEditingCell({ row, col });
        // Clear row selections when clicking on a cell to edit
        setSelectedRows(new Set());
    };

    const startResize = (index: number, e: React.MouseEvent) => {
        resizingColRef.current = index;
        document.body.style.cursor = 'col-resize';
        const startX = e.clientX;
        // Adjust index: +2 for checkbox and Sl # columns
        const th = tableRef.current?.querySelectorAll('th')[index + 2] as HTMLTableCellElement;
        const startWidth = th?.offsetWidth || 100;

        const onMouseMove = (e: MouseEvent) => {
            const newWidth = startWidth + (e.clientX - startX);
            if (th && newWidth > 50) {
                th.style.width = `${newWidth}px`;
            }
        };

        const onMouseUp = () => {
            resizingColRef.current = null;
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTableElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        
        // Save current state before pasting
        saveToUndoStack();

        let startRow: number;
        let startColIndex: number;

        if (selectedRows.size > 1) {
            // When multiple rows are selected, prioritize selected rows over editing cell
            const sortedSelectedRows = Array.from(selectedRows).sort((a, b) => a - b);
            startRow = sortedSelectedRows[0];
            startColIndex = 0;
        } else if (editingCell) {
            // Paste starting from the currently editing cell
            startRow = editingCell.row;
            startColIndex = columns.findIndex(c => c.key === editingCell.col);
        } else if (selectedRows.size > 0) {
            // Paste starting from the single selected row, first column
            const sortedSelectedRows = Array.from(selectedRows).sort((a, b) => a - b);
            startRow = sortedSelectedRows[0];
            startColIndex = 0;
        } else {
            // No cell editing and no rows selected, paste at top-left
            startRow = 0;
            startColIndex = 0;
        }

        const lines = text.split('\n').filter(Boolean);
        const delimiter = text.includes('\t') ? '\t' : ',';
        const parsedData = lines.map(line => line.split(delimiter));

        const updated = [...rows];

        while (updated.length < startRow + parsedData.length) {
            const emptyRow: any = {};
            columns.forEach(col => {
                emptyRow[col.key] = '';
            });
            updated.push(emptyRow);
        }

        // Accumulate validation errors outside the loop so all pasted cells are captured
        const newValidationErrors = { ...validationErrors };
        const loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];

        parsedData.forEach((line, i) => {
            const rowIndex = startRow + i;
            line.forEach((value, j) => {
                const colIndex = startColIndex + j;
                const colKey = columns[colIndex]?.key;
                if (!colKey) return;
                
                let cleanedValue = value.replace(/[\r\n]+/g, ' ');
                
                // Clean single quotes from Shipment ID values
                if (colKey === 'Shipment ID') {
                    cleanedValue = cleanedValue.replace(/'/g, '');
                }
                
                // Convert Facility values to uppercase for IN02 tab
                if (tabType === 'IN02' && colKey === 'Facility') {
                    cleanedValue = cleanedValue.toUpperCase();
                }
                
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [colKey]: cleanedValue
                };

                // Validate Load Group fields on paste
                if (tabType === 'IN02' && loadGroupFields.includes(colKey)) {
                    const cellKey = `${rowIndex}-${colKey}`;
                    if (cleanedValue && cleanedValue.trim() !== '' && !isValidLoadGroupValue(cleanedValue)) {
                        newValidationErrors[cellKey] = true;
                    } else {
                        delete newValidationErrors[cellKey];
                    }
                }
            });
        });

        setValidationErrors(newValidationErrors);
        setRows(updated);
        setEditingCell(null);
        setSelectedRows(new Set());
    };

    const toggleRowSelection = (rowIndex: number) => {
        const newSet = new Set(selectedRows);
        if (newSet.has(rowIndex)) {
            newSet.delete(rowIndex);
        } else {
            newSet.add(rowIndex);
        }
        setSelectedRows(newSet);
    };

    const addRow = () => {
        // Save current state before adding row
        saveToUndoStack();
        
        const newRow: any = {};
        columns.forEach(col => {
            newRow[col.key] = '';
        });
        setRows([...rows, newRow]);
    };

    const deleteSelected = () => {
        if (selectedRows.size === 0) return;

        // Save current state before deleting rows
        saveToUndoStack();

        const newRows = rows.filter((_, index) => !selectedRows.has(index));

        // Create a map to translate old row indices to new ones for kept rows
        const indexMap = new Map<number, number>();
        let newIndex = 0;
        for (let i = 0; i < rows.length; i++) {
            if (!selectedRows.has(i)) {
                indexMap.set(i, newIndex);
                newIndex++;
            }
        }

        // Adjust validation errors using the index map. This is more efficient
        // than a nested loop, especially with many rows and errors.
        const newValidationErrors: Record<string, boolean> = {};
        for (const key in validationErrors) {
            const parts = key.split('-');
            const oldRowIndex = parseInt(parts[0], 10);

            // If the error's old row index is in our map, it means it wasn't deleted
            if (indexMap.has(oldRowIndex)) {
                const newRowIndex = indexMap.get(oldRowIndex)!; // '!' is safe due to .has() check
                const colKey = parts.slice(1).join('-');
                newValidationErrors[`${newRowIndex}-${colKey}`] = true;
            }
        }

        setRows(newRows);
        setValidationErrors(newValidationErrors);
        setSelectedRows(new Set());
    };

    const handleCopySelectedRows = () => {
        if (selectedRows.size === 0) return;
        const selected = Array.from(selectedRows).sort((a, b) => a - b);
        const lines = selected.map(rowIndex =>
            columns.map((col: any) => rows[rowIndex][col.key] ?? '').join('\t')
        );
        const text = lines.join('\n');
        navigator.clipboard.writeText(text);
    };

    const bulkClearCells = (cellUpdates: { rowIndex: number; colKey: string }[]) => {
        // Save current state before making bulk changes
        saveToUndoStack();
        
        const newRows = [...rows];
        cellUpdates.forEach(({ rowIndex, colKey }) => {
            newRows[rowIndex] = { ...newRows[rowIndex], [colKey]: '' };
        });
        setRows(newRows);
    };

    const [jumpToPageError, setJumpToPageError] = useState<string | null>(null);
    const [paginationModel, setPaginationModel] = useState({
        page: 0, // Current page (0-based index)
        pageSize: 25, // Number of rows per page
    });

    const saveToUndoStack = useCallback(() => {
        const newState: UndoState = {
            rows: JSON.parse(JSON.stringify(rows)), // Deep copy
            currentPage: paginationModel.page
        };
        setUndoStack(prev => {
            const newStack = [newState, ...prev];
            return newStack.slice(0, maxUndoSteps); // Keep only last N states
        });
        // Clear redo stack when new action is performed
        setRedoStack([]);
    }, [rows, paginationModel.page]);

    const handleUndo = useCallback(() => {
        if (undoStack.length === 0) return false;
        
        // Get the previous state to restore
        const previousState = undoStack[0];
        
        // Save current state to redo stack before undoing
        const currentState: UndoState = {
            rows: JSON.parse(JSON.stringify(rows)),
            currentPage: paginationModel.page
        };
        setRedoStack(prev => [currentState, ...prev.slice(0, maxUndoSteps - 1)]);
        
        // Remove the state we're restoring from undo stack
        setUndoStack(prev => prev.slice(1));
        
        // Restore the previous state
        setRows(previousState.rows);
        setPaginationModel(prev => ({ ...prev, page: previousState.currentPage }));
        
        return true;
    }, [undoStack, rows, paginationModel.page]);

    const handleRedo = useCallback(() => {
        if (redoStack.length === 0) return false;
        
        // Get the next state to restore
        const nextState = redoStack[0];
        
        // Save current state to undo stack before redoing
        const currentState: UndoState = {
            rows: JSON.parse(JSON.stringify(rows)),
            currentPage: paginationModel.page
        };
        setUndoStack(prev => [currentState, ...prev.slice(0, maxUndoSteps - 1)]);
        
        // Remove the state we're restoring from redo stack
        setRedoStack(prev => prev.slice(1));
        
        // Restore the next state
        setRows(nextState.rows);
        setPaginationModel(prev => ({ ...prev, page: nextState.currentPage }));
        
        return true;
    }, [redoStack, rows, paginationModel.page]);

    // Add keyboard listener for Ctrl+Z and Ctrl+Y
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            } else if (e.ctrlKey && e.key === 'y') {
                e.preventDefault();
                handleRedo();
            }
        };
        
        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [handleUndo, handleRedo]);

    const handleJumpToPage = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const inputElement = event.target as HTMLInputElement;
            const pageNumber = Number(inputElement.value);
            const totalPages = Math.ceil(rows.length / paginationModel.pageSize);
            if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
                setJumpToPageError(`Page ${pageNumber} does not exist. Please enter a valid page number between 1 and ${totalPages}`);
                return;
            }
            setJumpToPageError(null);
            setPaginationModel((prev) => ({
                ...prev,
                page: pageNumber - 1, // Convert 1-based page number to 0-based index
            }));
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
            // Clear the error when the input is cleared
            const inputElement = event.target as HTMLInputElement;
            if (inputElement.value === '') {
                setJumpToPageError(null);
            }
        }
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPaginationModel((prev) => ({
            ...prev,
            pageSize: newPageSize, // Update the page size
            page: 0, // Reset to the first page
        }));
    };

    const totalPages = Math.ceil(rows.length / paginationModel.pageSize);
    const currentPage = paginationModel.page;
    const maxVisiblePages = 3;

    const createPageRange = () => {
        const pages = [];
        // Always show first page
        if (totalPages <= maxVisiblePages + 2) {
            // Show all pages if total pages are few
            for (let i = 0; i < totalPages; i++) pages.push(i);
        } else {
            pages.push(0); // First page
            let start = Math.max(1, currentPage - 1);
            let end = Math.min(totalPages - 2, currentPage + 1);
            if (currentPage <= 2) {
                end = 3;
            }
            if (currentPage >= totalPages - 3) {
                start = totalPages - 4;
            }
            if (start > 1) pages.push('...');
            for (let i = start; i <= end; i++) {
                if (i > 0 && i < totalPages - 1) pages.push(i);
            }
            if (end < totalPages - 2) pages.push('...');
            pages.push(totalPages - 1); // Last page
        }
        return pages;
    };

    const resetHistory = useCallback(() => {
        setUndoStack([]);
        setRedoStack([]);
    }, []);

    useEffect(() => {
        if (!isActive) return;
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            } else if (e.ctrlKey && e.key === 'y') {
                e.preventDefault();
                handleRedo();
            }
        };
        
        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [isActive, handleUndo, handleRedo]);

    return {
        rows,
        setRows,
        editingCell,
        setEditingCell,
        selectedRows,
        setSelectedRows,
        tableRef,
        resizingColRef,
        handleCellChange,
        handleClick,
        startResize,
        handlePaste,
        toggleRowSelection,
        addRow,
        deleteSelected,
        handleCopySelectedRows,
        bulkClearCells,
        handleUndo,
        handleRedo,
        undoStack,
        redoStack,
        resetHistory,
        jumpToPageError,
        setJumpToPageError,
        paginationModel,
        setPaginationModel,
        handleJumpToPage,
        handlePageSizeChange,
        currentPage,
        createPageRange,
        validationErrors,
        setValidationErrors
    };
}